import { Injectable } from '@angular/core';
import { UntypedFormControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { I18nTranslateService } from './i18n-translate.service';

type errorMessageFn = (controlValue: any) => string;

interface IValidationOpts {
  options?: {
    [key: string]: any;
  };
  errorMessage?: string | errorMessageFn;
}

@Injectable()
export class ValidationService {
  readonly defaultOpts: IValidationOpts = {
    options: {},
    errorMessage: null,
  };

  readonly DEFAULT_FIELD_NAME = 'common_error:validation.this_field';

  constructor(private i18nService: I18nTranslateService) {}

  private getErrorMessage(controlValue: any, errorMessage?: any): string {
    if (typeof errorMessage === 'string') {
      return errorMessage;
    } else if (typeof errorMessage === 'function') {
      return errorMessage(controlValue);
    }

    return null;
  }

  custom = (opts: IValidationOpts = this.defaultOpts): ValidatorFn => {
    return (control: UntypedFormControl): ValidationErrors => {
      const input = (control.value as string) || '';
      if (opts.options.func(input)) {
        const errorMessage =
          this.getErrorMessage(input, opts.errorMessage) ||
          this.i18nService.t('common_error:validation.invalid', {
            name: this.i18nService.t(this.DEFAULT_FIELD_NAME),
          });
        return {
          custom: errorMessage,
        };
      }

      return null;
    };
  };

  match = (opts: IValidationOpts = this.defaultOpts): ValidatorFn => {
    return (control: UntypedFormControl): ValidationErrors => {
      const input = control.value as string;
      if (!opts.options.regex.test(input)) {
        const errorMessage =
          this.getErrorMessage(input, opts.errorMessage) ||
          this.i18nService.t('common_error:validation.match_pattern', {
            name: this.i18nService.t(this.DEFAULT_FIELD_NAME),
          });
        return {
          match: errorMessage,
        };
      }

      return null;
    };
  };

  required = (opts: IValidationOpts = this.defaultOpts): ValidatorFn => {
    return (control: UntypedFormControl): ValidationErrors => {
      const input = control.value?.trim?.() ?? control.value;
      if (!input) {
        const errorMessage =
          this.getErrorMessage(input, opts.errorMessage) ||
          this.i18nService.t('common_error:validation.required', {
            name: this.i18nService.t(this.DEFAULT_FIELD_NAME),
          });
        return { required: errorMessage };
      }

      return null;
    };
  };

  minLength = (opts: IValidationOpts = this.defaultOpts): ValidatorFn => {
    return (control: UntypedFormControl): ValidationErrors => {
      const min = opts?.options?.len ?? 0;
      const input = control.value.trim() as string;
      if (input && input.length < min) {
        const errorMessage =
          this.getErrorMessage(input, opts.errorMessage) ||
          this.i18nService.t('common_error:validation.min_length', {
            name: this.i18nService.t(this.DEFAULT_FIELD_NAME),
            length: min,
          });
        return { minLength: errorMessage };
      }

      return null;
    };
  };

  maxLength = (opts: IValidationOpts = this.defaultOpts): ValidatorFn => {
    return (control: UntypedFormControl): ValidationErrors => {
      const max = opts?.options?.len ?? Number.MAX_SAFE_INTEGER;
      if (!control.value) {
        return null;
      }
      const input = control.value.trim() as string;
      if (input && input.length > max) {
        const errorMessage =
          this.getErrorMessage(input, opts.errorMessage) ||
          this.i18nService.t('common_error:validation.max_length', {
            name: this.i18nService.t(this.DEFAULT_FIELD_NAME),
            length: max,
          });
        return { maxLength: errorMessage };
      }

      return null;
    };
  };

  duplicate = (opts: IValidationOpts = this.defaultOpts): ValidatorFn => {
    return (control: UntypedFormControl): ValidationErrors => {
      const items = opts?.options?.items ?? [];
      const input = control.value as string;
      if (input && items.indexOf(input) > -1) {
        const errorMessage =
          this.getErrorMessage(input, opts.errorMessage) ||
          this.i18nService.t('common_error:validation.duplicate', {
            name: this.i18nService.t(this.DEFAULT_FIELD_NAME),
          });
        return { duplicate: errorMessage };
      }

      return null;
    };
  };

  email = (opts: IValidationOpts = this.defaultOpts): ValidatorFn => {
    return (control: UntypedFormControl): ValidationErrors => {
      const input = control.value as string;
      const emailRegex = opts.options.domain
        ? new RegExp(
            `^([\\w-]+(?:\\.[\\w-]+)*)@${opts.options.domain.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')}`,
            'i',
          )
        : /^([\w-]+(?:\.[\w-]+)*)@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$/i;
      if (!emailRegex.test(input)) {
        const errorMessage =
          this.getErrorMessage(input, opts.errorMessage) || this.i18nService.t('common_error:validation.is_email');
        return {
          email: errorMessage,
        };
      }

      return null;
    };
  };

  /**
   * password rules: at least 5 chars, at least one uppercase, on numberic
   * can include special chars: !@#$%^&*_-
   */
  password = (opts: IValidationOpts = this.defaultOpts): ValidatorFn => {
    return (control: UntypedFormControl): ValidationErrors => {
      const skipPasswordValidation: () => boolean = opts?.options?.skipPasswordValidation ?? (() => false);
      if (skipPasswordValidation()) {
        return null;
      }
      const value = control.value as string;
      const regex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)[A-Za-z\d!@#$%^&*_+-]{8,128}$/;
      if (value.length < 8 || value.length > 128) {
        return { password: this.i18nService.t('common_error:validation.password_length') };
      } else if (!regex.test(value)) {
        return { password: this.i18nService.t('common_error:validation.password_format') };
      }

      return null;
    };
  };

  confirmPassword = (opts: IValidationOpts = this.defaultOpts): ValidatorFn => {
    return (control: UntypedFormControl): ValidationErrors => {
      const passwordControl = control.root.get('password');

      if (!passwordControl || passwordControl.value !== control.value) {
        return { confirmPassword: this.i18nService.t('common_error:validation.confirm_password') };
      }

      return null;
    };
  };
}
